import {typeConverter, useFirestore, useFirestoreResults, useFirestoreSnapshot} from '@goschool/react-firebase'
import type {DocumentReference} from 'firebase/firestore'
import {collection, doc, orderBy, query} from 'firebase/firestore'
import {useMemo} from 'react'
import type {Organization, OrganizationInstructor} from '../Organization'
import {isActiveSubscription} from '../Organization'

export function useOrganizations() {
  const firestore = useFirestore()
  const q = useMemo(
    () =>
      query(
        collection(firestore, 'organizations').withConverter(
          typeConverter<Organization>()
        ),
        orderBy('name', 'asc')
      ),
    [firestore]
  )

  return useFirestoreResults(q)
}

/**
 * Mirrors firestore.rules: an instructor may create a course in an organization
 * that has an active subscription, or when they hold one of their own there.
 */
export function useCanCreateCourse(
  organizationRef: DocumentReference | null | undefined,
  uid: string | null | undefined,
  roles: string[]
) {
  const typedOrganizationRef = useMemo(
    () => organizationRef?.withConverter(typeConverter<Organization>()),
    [organizationRef]
  )
  const membershipRef = useMemo(
    () => organizationRef != null && uid != null
      ? doc(organizationRef, 'instructors', uid).withConverter(typeConverter<OrganizationInstructor>())
      : undefined,
    [organizationRef, uid]
  )
  const organization = useFirestoreSnapshot(roles.includes('instructor') ? typedOrganizationRef : undefined)
  const membership = useFirestoreSnapshot(roles.includes('instructor') ? membershipRef : undefined)

  if (roles.includes('admin')) {
    return true
  }
  if (!roles.includes('instructor')) {
    return false
  }
  return isActiveSubscription(organization?.data()?.subscription)
    || isActiveSubscription(membership?.data()?.subscription)
}
